import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AppShell from '../components/AppShell';
import api from '../lib/api';
import { LoadingBlock } from '../components/States';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import StatusPill from '../components/StatusPill';
import { ArrowLeft, ShoppingBag, CreditCard, MapPin, User, Truck, Clock } from 'lucide-react';
import { toast } from 'sonner';

import { errMsg } from '../lib/errors';
const NEXT_ACTIONS = {
    pending: [{ status: 'confirmed', label: 'Confirm Order' }, { status: 'cancelled', label: 'Cancel', destructive: true }],
    confirmed: [{ status: 'packed', label: 'Mark Packed' }, { status: 'cancelled', label: 'Cancel', destructive: true }],
    packed: [{ status: 'shipped', label: 'Ship Order', tracking: true }, { status: 'cancelled', label: 'Cancel', destructive: true }],
    shipped: [{ status: 'out_for_delivery', label: 'Out for Delivery' }, { status: 'returned', label: 'Return' }],
    out_for_delivery: [{ status: 'delivered', label: 'Mark Delivered' }, { status: 'returned', label: 'Return' }],
    delivered: [{ status: 'returned', label: 'Return' }, { status: 'refunded', label: 'Refund' }],
    cancelled: [{ status: 'refunded', label: 'Refund' }],
    returned: [{ status: 'refunded', label: 'Refund' }],
    refunded: [],
};

export default function OrderDetail() {
    const { id } = useParams();
    const nav = useNavigate();
    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(true);
    const [action, setAction] = useState(null);
    const [notes, setNotes] = useState('');
    const [tracking, setTracking] = useState({ carrier: '', awb: '', url: '' });

    const load = async () => {
        try {
            const r = await api.get(`/orders/${id}`);
            setOrder(r.data);
        } catch { toast.error('Order not found'); nav('/dharmikdashboard/orders'); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, [id]);

    const openAction = (a) => { setAction(a); setNotes(''); setTracking({ carrier: '', awb: '', url: '' }); };

    const submit = async () => {
        try {
            const body = { to_status: action.status, notes };
            if (action.tracking) {
                body.tracking_carrier = tracking.carrier;
                body.tracking_awb = tracking.awb;
                body.tracking_url = tracking.url;
            }
            await api.post(`/orders/${id}/transition`, body);
            toast.success(`Order ${action.label.toLowerCase()}`);
            setAction(null); load();
        } catch (e) { toast.error(errMsg(e, 'Failed')); }
    };

    const mockPay = async (simulate) => {
        try {
            await api.post('/payments/mock', { order_id: id, simulate });
            toast.success(simulate === 'success' ? 'Payment marked successful' : 'Payment marked failed');
            load();
        } catch (e) { toast.error(errMsg(e, 'Failed')); }
    };

    if (loading || !order) return <AppShell pageTitle="Order"><LoadingBlock /></AppShell>;

    const actions = NEXT_ACTIONS[order.status] || [];

    return (
        <AppShell pageTitle={`Order ${order.order_number}`} pageActions={
            <Button variant="outline" onClick={() => nav('/dharmikdashboard/orders')} data-testid="back-to-orders-button"><ArrowLeft className="h-4 w-4 mr-2" />Back</Button>
        }>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
                <div className="lg:col-span-8 space-y-4">
                    <Card className="p-4">
                        <div className="flex items-center justify-between mb-3">
                            <div>
                                <div className="text-sm font-semibold">Order Status</div>
                                <div className="mt-1 flex gap-2"><StatusPill status={order.status} /><StatusPill status={order.payment_status} /></div>
                            </div>
                            <div className="flex flex-wrap gap-2" data-testid="order-status-actions">
                                {actions.map(a => (
                                    <Button key={a.status} variant={a.destructive ? 'outline' : 'default'} onClick={() => openAction(a)} data-testid={`order-action-${a.status}`}
                                        style={a.destructive ? {} : { background: 'hsl(142 71% 45%)', color: 'white' }}>
                                        {a.label}
                                    </Button>
                                ))}
                            </div>
                        </div>
                        {order.tracking?.awb && (
                            <div className="mt-2 text-xs text-neutral-700 flex items-center gap-2">
                                <Truck className="h-3.5 w-3.5" />
                                Tracking: <span className="font-mono">{order.tracking.carrier} — {order.tracking.awb}</span>
                                {order.tracking.url && <a href={order.tracking.url} target="_blank" rel="noreferrer" className="text-blue-600 underline">Track</a>}
                            </div>
                        )}
                    </Card>

                    <Card className="p-4">
                        <div className="text-sm font-semibold mb-3 flex items-center gap-2"><ShoppingBag className="h-4 w-4" /> Items ({order.items?.length})</div>
                        <div className="space-y-2">
                            {order.items?.map(it => (
                                <div key={it.product_id} className="flex items-center gap-3 py-2 border-b last:border-0">
                                    <div className="h-12 w-12 rounded bg-neutral-100 overflow-hidden shrink-0">
                                        {it.image && <img src={it.image} alt={it.name} className="h-full w-full object-cover" onError={(e) => e.currentTarget.style.display = 'none'} />}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="font-medium truncate">{it.name}</div>
                                        <div className="text-xs text-neutral-500 font-mono">{it.sku}</div>
                                    </div>
                                    <div className="text-sm text-neutral-600 tabular-nums">{it.qty} × ₹{it.unit_price}</div>
                                    <div className="text-sm font-medium tabular-nums w-24 text-right">₹{it.line_total}</div>
                                </div>
                            ))}
                        </div>
                        <div className="mt-4 pt-4 border-t space-y-1 text-sm">
                            <div className="flex justify-between"><span className="text-neutral-600">Subtotal</span><span className="tabular-nums">₹{order.totals?.subtotal}</span></div>
                            <div className="flex justify-between"><span className="text-neutral-600">Shipping ({order.totals?.total_weight_kg} kg)</span><span className="tabular-nums">₹{order.totals?.shipping}</span></div>
                            <div className="flex justify-between text-base font-semibold pt-2 border-t mt-2"><span>Grand Total</span><span className="tabular-nums">₹{order.totals?.grand_total}</span></div>
                        </div>
                    </Card>

                    <Card className="p-4">
                        <div className="text-sm font-semibold mb-3 flex items-center gap-2"><Clock className="h-4 w-4" /> Timeline</div>
                        <div className="space-y-3" data-testid="order-timeline">
                            {[...(order.timeline || [])].reverse().map((t) => (
                                <div key={t.id} className="flex gap-3">
                                    <div className="flex flex-col items-center">
                                        <div className="h-2 w-2 rounded-full bg-green-500 mt-1.5" />
                                        <div className="w-px flex-1 bg-neutral-200 mt-1" />
                                    </div>
                                    <div className="flex-1 pb-3">
                                        <div className="text-sm">
                                            {t.from_status && t.from_status !== t.to_status ? (
                                                <><span className="text-neutral-600">{t.from_status}</span> <span>→</span> <span className="font-medium capitalize">{t.to_status?.replace(/_/g, ' ')}</span></>
                                            ) : (
                                                <span className="font-medium capitalize">{t.to_status?.replace(/_/g, ' ')}</span>
                                            )}
                                        </div>
                                        {t.notes && <div className="text-xs text-neutral-600 mt-0.5">{t.notes}</div>}
                                        <div className="text-xs text-neutral-500 mt-0.5">{new Date(t.at).toLocaleString()} · by {t.by || 'system'}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </Card>
                </div>

                <div className="lg:col-span-4 space-y-4">
                    <Card className="p-4">
                        <div className="text-sm font-semibold mb-2 flex items-center gap-2"><User className="h-4 w-4" /> Customer</div>
                        <div className="text-sm space-y-0.5">
                            <div className="font-medium">{order.customer_snapshot?.name}</div>
                            <div className="text-neutral-600">{order.customer_snapshot?.mobile}</div>
                            <div className="text-neutral-600 text-xs">{order.customer_snapshot?.email}</div>
                            <div className="text-xs mt-1">Type: <span className="capitalize">{order.customer_snapshot?.type}</span>{order.customer_snapshot?.level ? ` · Level ${order.customer_snapshot.level}` : ''}</div>
                        </div>
                    </Card>
                    <Card className="p-4">
                        <div className="text-sm font-semibold mb-2 flex items-center gap-2"><MapPin className="h-4 w-4" /> Shipping Address</div>
                        <div className="text-sm text-neutral-800 space-y-0.5">
                            <div>{order.shipping_address?.name}, {order.shipping_address?.mobile}</div>
                            <div>{order.shipping_address?.line1}</div>
                            {order.shipping_address?.line2 && <div>{order.shipping_address.line2}</div>}
                            <div>{order.shipping_address?.city} {order.shipping_address?.district}</div>
                            <div>{order.shipping_address?.state} — {order.shipping_address?.pincode}</div>
                            <div>{order.shipping_address?.country}</div>
                        </div>
                    </Card>
                    <Card className="p-4">
                        <div className="text-sm font-semibold mb-2 flex items-center gap-2"><CreditCard className="h-4 w-4" /> Payment</div>
                        <div className="space-y-2 text-sm">
                            <div className="flex items-center justify-between"><span>Status</span><StatusPill status={order.payment_status} /></div>
                            {order.payment?.txn_id && <div className="text-xs text-neutral-600">Txn: <span className="font-mono">{order.payment.txn_id}</span></div>}
                            {order.payment_status !== 'paid' && (
                                <div className="pt-2 border-t space-y-2">
                                    <div className="text-xs text-neutral-500">Simulate payment (mock):</div>
                                    <div className="flex gap-2">
                                        <Button size="sm" onClick={() => mockPay('success')} data-testid="mock-pay-success" style={{ background: 'hsl(142 71% 45%)', color: 'white' }}>Success</Button>
                                        <Button size="sm" variant="outline" onClick={() => mockPay('failure')} data-testid="mock-pay-failure">Failure</Button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </Card>
                </div>
            </div>

            <Dialog open={!!action} onOpenChange={(v) => { if (!v) setAction(null); }}>
                <DialogContent>
                    <DialogHeader><DialogTitle>{action?.label}</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        {action?.tracking && (
                            <div className="space-y-2">
                                <div><Label>Carrier</Label><Input value={tracking.carrier} onChange={e => setTracking({ ...tracking, carrier: e.target.value })} placeholder="Delhivery, Bluedart, Ecom, ..." data-testid="tracking-carrier-input" /></div>
                                <div><Label>AWB / Tracking Number</Label><Input value={tracking.awb} onChange={e => setTracking({ ...tracking, awb: e.target.value })} data-testid="tracking-awb-input" /></div>
                                <div><Label>Tracking URL (optional)</Label><Input value={tracking.url} onChange={e => setTracking({ ...tracking, url: e.target.value })} placeholder="https://..." /></div>
                            </div>
                        )}
                        <div><Label>Notes (optional)</Label><Textarea rows={3} value={notes} onChange={e => setNotes(e.target.value)} data-testid="transition-notes-input" /></div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setAction(null)}>Cancel</Button>
                        <Button onClick={submit} data-testid="confirm-transition-button" style={{ background: action?.destructive ? 'hsl(0 84% 60%)' : 'hsl(142 71% 45%)', color: 'white' }}>Confirm</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </AppShell>
    );
}
